import React, { useMemo } from 'react';
import { Table, Button, Space, Card, Tag, Popconfirm, Row, Col, Statistic, Alert, Typography, Tooltip, Badge } from 'antd';
import {
  DesktopOutlined, CheckCircleOutlined, ExclamationCircleOutlined, ClockCircleOutlined,
  ThunderboltOutlined, CloudDownloadOutlined, DeleteOutlined, EditOutlined, ApiOutlined,
  PlusOutlined, UserDeleteOutlined
} from '@ant-design/icons';
import { formatDateStr, formatTimeStr } from '../attendanceUtils';
import { SERVER_URL } from '../../../../Service/apiConfig';

const { Text, Paragraph } = Typography;

export default function DeviceSetupTab({
  devices,
  rawLogs,
  serverNetInfo,
  devLoading,
  pingingDevId,
  onReload,
  onOpenDevModal,
  onDeleteDev,
  onPingDevice,
  onOpenPushModal,
  onOpenImportModal,
  onDeleteAllMappings,
  onRequestAttendanceImport,
  onClearDeviceUsers,
  onClearAllLogs,
  onSimulatePunch
}) {
  // Device Statistics
  const devStats = useMemo(() => {
    const total = devices.length;
    let online = 0;
    let offline = 0;
    devices.forEach(d => {
      if (d.status === 'ONLINE') online++;
      else offline++;
    });
    return {
      total,
      online,
      offline,
      totalPunches: rawLogs.length
    };
  }, [devices, rawLogs]);

  // Device Columns
  const deviceColumns = [
    {
      title: 'Device Name',
      dataIndex: 'device_name',
      key: 'device_name',
      render: (name) => <strong>{name}</strong>
    },
    {
      title: 'Device Model / Type',
      dataIndex: 'device_type',
      key: 'device_type',
      render: (type) => <Tag color="geekblue">{type || 'eSSL K30'}</Tag>
    },
    {
      title: 'Serial Number',
      dataIndex: 'serial_number',
      key: 'serial_number',
      render: (sn) => <Text code>{sn || 'N/A'}</Text>
    },
    {
      title: 'Device ID / No.',
      dataIndex: 'machine_number',
      key: 'machine_number',
      align: 'center',
      render: (num) => <Tag color="blue">ID: {num !== undefined && num !== null ? num : 1}</Tag>
    },
    {
      title: 'COMM Key',
      dataIndex: 'comm_key',
      key: 'comm_key',
      align: 'center',
      render: (key) => <Text code>{key || '0'}</Text>
    },
    {
      title: 'IP Address & Port',
      dataIndex: 'ip_address',
      key: 'ip_address',
      render: (ip, rec) => (
        <span>
          {ip || 'Dynamic ADMS'} {rec.port ? `:${rec.port}` : ''}
        </span>
      )
    },
    {
      title: 'Location / Branch',
      dataIndex: 'location',
      key: 'location',
      render: (loc) => loc || <Text type="secondary">Main Office</Text>
    },
    {
      title: 'Live Status',
      dataIndex: 'status',
      key: 'status',
      render: (st, record) => (
        <Space size="small">
          <Tag color={st === 'ONLINE' ? 'green' : 'red'} style={{ fontWeight: 600, padding: '2px 8px' }}>
            {st === 'ONLINE' ? '● ONLINE' : '○ OFFLINE'}
          </Tag>
          <Tooltip title="Test Live Connection to Device">
            <Button
              size="small"
              type="text"
              icon={<ThunderboltOutlined style={{ color: st === 'ONLINE' ? '#52c41a' : '#ff4d4f' }} />}
              loading={pingingDevId === record.device_id}
              onClick={() => onPingDevice(record)}
            />
          </Tooltip>
        </Space>
      )
    },
    {
      title: 'Last Sync / Heartbeat',
      dataIndex: 'last_sync_at',
      key: 'last_sync_at',
      render: (st) => st ? (
        <span>{formatDateStr(st)} {formatTimeStr(st)}</span>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>No sync recorded</Text>
      )
    },
    {
      title: 'Actions',
      key: 'act',
      align: 'center',
      render: (_, record) => (
        <Space size="small" wrap>
          <Tooltip title="Push User Data to this Device">
            <Button
              type="primary"
              size="small"
              style={{ background: '#52c41a', borderColor: '#52c41a' }}
              icon={<ThunderboltOutlined />}
              onClick={() => onOpenPushModal(record.device_id)}
            />
          </Tooltip>
          <Tooltip title="Pull Attendance Logs from this Device">
            <Button
              type="default"
              size="small"
              icon={<CloudDownloadOutlined />}
              onClick={() => onRequestAttendanceImport({ device_id: record.device_id, serial_number: record.serial_number })}
            />
          </Tooltip>
          <Popconfirm
            title="Clear all pushed users from this machine?"
            description="This will delete all enrolled users directly from the physical machine memory."
            onConfirm={() => onClearDeviceUsers && onClearDeviceUsers({ device_id: record.device_id, serial_number: record.serial_number })}
            okText="Yes, Clear Users"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Clear/Delete All Users from Physical Machine">
              <Button
                type="default"
                danger
                size="small"
                icon={<UserDeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
          <Tooltip title="Test Connection">
            <Button
              type="default"
              size="small"
              icon={<ThunderboltOutlined style={{ color: '#1890ff' }} />}
              loading={pingingDevId === record.device_id}
              onClick={() => onPingDevice(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Device Settings">
            <Button type="default" size="small" icon={<EditOutlined />} onClick={() => onOpenDevModal(record)} />
          </Tooltip>
          <Popconfirm title="Delete device?" onConfirm={() => onDeleteDev(record.device_id)}>
            <Tooltip title="Delete Device">
              <Button type="default" danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Logs Columns
  const logColumns = [
    {
      title: 'Log ID',
      dataIndex: 'log_id',
      key: 'log_id',
      width: 75,
      render: (id) => <Tag color="default">#{id}</Tag>
    },
    {
      title: 'Biometric Machine PIN',
      dataIndex: 'biometric_user_id',
      key: 'biometric_user_id',
      render: (pin) => <Tag color="geekblue" style={{ fontWeight: 'bold' }}>PIN: {pin}</Tag>
    },
    {
      title: 'Identified Individual',
      key: 'mapped_person',
      render: (_, record) => {
        if (!record.mapped_person_name) {
          return <Tag color="warning">Unmapped PIN</Tag>;
        }
        return (
          <div>
            <strong>{record.mapped_person_name}</strong>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              {record.mapped_person_type} {record.cluster_name ? `• ${record.cluster_name}` : ''}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Physical Punch Timestamp',
      dataIndex: 'punch_time',
      key: 'punch_time',
      render: (t) => (
        <span>
          <ClockCircleOutlined style={{ marginRight: 6, color: '#1890ff' }} />
          <strong>{formatDateStr(t)}</strong> {formatTimeStr(t)}
        </span>
      )
    },
    {
      title: 'Punch Direction / State',
      dataIndex: 'punch_state',
      key: 'punch_state',
      render: (st) => {
        const isOut = st === 'OUT' || st === '1' || st === '5' || String(st).toUpperCase().includes('OUT');
        return (
          <Tag color={isOut ? 'volcano' : 'green'} style={{ fontWeight: 600 }}>
            {isOut ? 'OUT (Check-Out)' : 'IN (Check-In)'}
          </Tag>
        );
      }
    },
    {
      title: 'Raw Machine Stream Packet',
      dataIndex: 'raw_payload',
      key: 'raw_payload',
      render: (raw) => <Text code style={{ fontSize: 11 }}>{raw || '-'}</Text>
    }
  ];

  return (
    <div>
      {/* Device Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card bodyStyle={{ padding: 16 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic title="Total Registered Devices" value={devStats.total} prefix={<DesktopOutlined style={{ color: '#1890ff' }} />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bodyStyle={{ padding: 16 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic title="Online & Connected" value={devStats.online} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bodyStyle={{ padding: 16 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic title="Offline / Disconnected" value={devStats.offline} valueStyle={{ color: devStats.offline > 0 ? '#ff4d4f' : 'inherit' }} prefix={<ExclamationCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bodyStyle={{ padding: 16 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic title="Live Punch Logs" value={devStats.totalPunches} valueStyle={{ color: '#722ed1' }} prefix={<ClockCircleOutlined />} />
          </Card>
        </Col>
      </Row>

      {/* FULL-WIDTH CARD: Registered Biometric Devices */}
      <Card
        title={
          <Space align="center" size="middle">
            <DesktopOutlined style={{ color: 'var(--primary-color)', fontSize: 18 }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>Registered Biometric Devices</span>
            <Tag color="blue">{devices.length} {devices.length === 1 ? 'Device' : 'Devices'}</Tag>
            <Tag color="green" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 500 }}>
              <Badge status="processing" color="#52c41a" /> Auto-Updating (8s)
            </Tag>
          </Space>
        }
        extra={
          <Space wrap>
            <Button icon={<CloudDownloadOutlined />} onClick={() => onRequestAttendanceImport({})} loading={devLoading}>
              Import Attendance from Device
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => onOpenDevModal()}>
              Add Device
            </Button>
          </Space>
        }
        style={{ marginBottom: 16, background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
      >
        <Table
          dataSource={devices}
          rowKey="device_id"
          size="middle"
          pagination={devices.length > 5 ? { pageSize: 5 } : false}
          bordered
          scroll={{ x: 'max-content' }}
          columns={deviceColumns}
        />
      </Card>

      {/* FULL-WIDTH CARD: Direct Machine ADMS Endpoint */}
      <Card
        title={
          <Space align="center" size="middle">
            <ApiOutlined style={{ color: 'var(--primary-color)', fontSize: 18 }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>Direct Machine ADMS Endpoint Configuration</span>
            <Tag color="purple">eSSL K30 / Identix / ZKTeco Compatible</Tag>
          </Space>
        }
        extra={<Tag color="green" style={{ fontWeight: 600, padding: '2px 10px' }}>● ADMS Service Listening on Port 5000 / 80</Tag>}
        style={{ marginBottom: 16, background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
      >
        <Paragraph style={{ marginBottom: 16, fontSize: 14 }}>
          Configure your physical <strong>eSSL K30 / Identix Biometric Machine</strong> to push attendance logs directly to your server in real-time. Follow either <strong>Option 1 (Local LAN)</strong> or <strong>Option 2 (Cloud Domain)</strong>:
        </Paragraph>

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {/* Local LAN Setup Box */}
          <Col xs={24} lg={12}>
            <div style={{ background: 'rgba(24, 144, 255, 0.04)', padding: 16, borderRadius: 8, border: '1.5px solid rgba(24, 144, 255, 0.3)', height: '100%' }}>
              <Typography.Title level={5} style={{ color: '#1890ff', marginTop: 0, marginBottom: 12 }}>
                🏠 Option 1: Local Network Setup (Same Wi-Fi / LAN)
              </Typography.Title>
              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '8px 12px', fontSize: 13, marginBottom: 12 }}>
                <Text strong>Server IP Address:</Text>
                <Text code copyable style={{ fontSize: 14, fontWeight: 'bold', color: '#096dd9' }}>
                  {serverNetInfo?.primaryLocalIp || '192.168.1.189'}
                </Text>

                <Text strong>Server Port:</Text>
                <Text code copyable style={{ fontSize: 14, fontWeight: 'bold' }}>5000</Text>

                <Text strong>Enable Domain Name:</Text>
                <Tag color="default">OFF / Disabled</Tag>

                <Text strong>Enable HTTPS / SSL:</Text>
                <Tag color="default">OFF / Disabled</Tag>

                <Text strong>Enable Cloud Server:</Text>
                <Tag color="success">ON / Enabled</Tag>
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                *Recommended when your biometric device is connected to the same router as this server.
              </Text>
            </div>
          </Col>

          {/* Cloud Domain Setup Box */}
          <Col xs={24} lg={12}>
            <div style={{ background: 'rgba(114, 46, 209, 0.04)', padding: 16, borderRadius: 8, border: '1.5px solid rgba(114, 46, 209, 0.3)', height: '100%' }}>
              <Typography.Title level={5} style={{ color: '#722ed1', marginTop: 0, marginBottom: 12 }}>
                🌐 Option 2: Domain / Public Server Setup (Internet)
              </Typography.Title>
              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '8px 12px', fontSize: 13, marginBottom: 12 }}>
                <Text strong>Server Address / Domain:</Text>
                <Text code copyable style={{ fontSize: 13, fontWeight: 'bold', color: '#531dab' }}>
                  {SERVER_URL.replace(/https?:\/\//, '').split(':')[0] || 'api.mahamayafoundation.org'}
                </Text>

                <Text strong>Server Port:</Text>
                <Text code copyable style={{ fontSize: 14, fontWeight: 'bold' }}>80 (or 5000)</Text>

                <Text strong>Enable Domain Name:</Text>
                <Tag color="purple">ON / Enabled</Tag>

                <Text strong>ADMS Push Path:</Text>
                <Text code>/iclock/cdata</Text>

                <Text strong>Enable Cloud Server:</Text>
                <Tag color="success">ON / Enabled</Tag>
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                *Use this when your biometric machine is at a remote branch location connecting over internet.
              </Text>
            </div>
          </Col>
        </Row>

        <Alert
          message="Important Machine Setup Checklist (eSSL K30 / Identix)"
          description={
            <div style={{ fontSize: 13, lineHeight: '1.8' }}>
              <div><strong>1. On the Machine Menu:</strong> Go to <strong>Menu &rarr; Comm. Settings &rarr; Cloud Server / ADMS</strong>.</div>
              <div><strong>2. Server Address / IP:</strong> Type <code>{serverNetInfo?.primaryLocalIp || '192.168.1.189'}</code> (for LAN) or <code>{SERVER_URL.replace(/https?:\/\//, '').split(':')[0] || 'api.mahamayafoundation.org'}</code> (for Domain). ⚠️ <em>Do NOT type "http://" or "/iclock/cdata" inside the address box.</em></div>
              <div><strong>3. Server Port:</strong> Type <code>5000</code> (LAN) or <code>80</code> (Domain).</div>
              <div><strong>4. Turn ON:</strong> Set <strong>Enable Cloud Server / ADMS</strong> to <strong>ON</strong>.</div>
              <div><strong>5. Network Check:</strong> In <strong>Menu &rarr; Comm. Settings &rarr; Ethernet</strong>, ensure DNS 1 is set to <code>8.8.8.8</code>.</div>
              <div><strong>6. Restart Device:</strong> After saving, restart the biometric device once. The machine will connect and show a globe/cloud icon on its screen!</div>
            </div>
          }
          type="info"
          showIcon
        />
      </Card>

      {/* FULL-WIDTH CARD: Live Biometric Raw Punch Stream */}
      <Card
        title={
          <Space align="center" size="middle">
            <ClockCircleOutlined style={{ color: 'var(--primary-color)', fontSize: 18 }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>Live Biometric Raw Punch Stream</span>
            <Tag color="geekblue">{rawLogs.length} Records Streamed</Tag>
          </Space>
        }
        extra={
          <Space wrap>
            <Button
              type="dashed"
              icon={<ThunderboltOutlined style={{ color: '#52c41a' }} />}
              onClick={onSimulatePunch}
            >
              Simulate Test Punch
            </Button>
            <Popconfirm
              title="Clear all stored raw punch logs?"
              description="This will empty the live stream audit trail."
              onConfirm={onClearAllLogs}
              okText="Clear Logs"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button danger icon={<DeleteOutlined />}>
                Clear Raw Stream
              </Button>
            </Popconfirm>
          </Space>
        }
        style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
      >
        <Table
          dataSource={rawLogs}
          rowKey="log_id"
          size="middle"
          pagination={{ pageSize: 10, showSizeChanger: true }}
          bordered
          scroll={{ x: 'max-content' }}
          columns={logColumns}
        />
      </Card>
    </div>
  );
}
